import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { User } from '../../types';
import { API_BASE_URL } from '../../config/api';
import UserChatDrawer from '../UserChatDrawer';
import TesterUsersTable from '../TesterUsersTable';
import { notifyTesterUnreadChanged } from '../../utils/tester';

const PAGE_SIZE = 10;

const BetaUsersTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatUser, setChatUser] = useState<User | null>(null);
  const [unreadByUserId, setUnreadByUserId] = useState<Record<string, number>>({});

  const [searchId, setSearchId] = useState(searchParams.get('id') || '');
  const [searchEmail, setSearchEmail] = useState(searchParams.get('email') || '');
  const [searchFirstName, setSearchFirstName] = useState(searchParams.get('firstName') || '');
  const [searchLastName, setSearchLastName] = useState(searchParams.get('lastName') || '');
  const [isSearching, setIsSearching] = useState(
    Boolean(
      searchParams.get('id') ||
      searchParams.get('email') ||
      searchParams.get('firstName') ||
      searchParams.get('lastName')
    )
  );

  const fetchBetaUsers = useCallback(async (page: number) => {
    setLoadingUsers(true);
    try {
      const params: Record<string, string | number> = {
        tester: 'beta',
        page,
        limit: PAGE_SIZE,
        sortColumn: 'email',
      };

      const id = searchParams.get('id');
      const email = searchParams.get('email');
      const firstName = searchParams.get('firstName');
      const lastName = searchParams.get('lastName');

      if (id) params.id = id;
      if (email) params.email = email;
      if (firstName) params.firstName = firstName;
      if (lastName) params.lastName = lastName;

      const hasSearch = Boolean(id || email || firstName || lastName);

      const response = await axios.get(`${API_BASE_URL}/users`, {
        withCredentials: true,
        params,
      });
      setUsers(response.data.users || []);
      setUsersTotal(response.data.totalUsers || 0);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(page);
      setIsSearching(hasSearch);
      setError(response.data.totalUsers === 0 && hasSearch
        ? 'No users found matching the current filters'
        : null
      );
    } catch (err: any) {
      console.error('Error fetching beta users:', err);
      setError(err.response?.data?.message || 'Failed to load beta users');
    } finally {
      setLoadingUsers(false);
    }
  }, [searchParams]);

  const fetchUnread = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/chat/unread/testers`, {
        withCredentials: true,
      });
      setUnreadByUserId(response.data.beta?.byUserId || {});
    } catch (_err) {
      setUnreadByUserId({});
    }
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const nextParams = new URLSearchParams();
    if (searchId.trim()) nextParams.set('id', searchId.trim());
    if (searchEmail.trim()) nextParams.set('email', searchEmail.trim());
    if (searchFirstName.trim()) nextParams.set('firstName', searchFirstName.trim());
    if (searchLastName.trim()) nextParams.set('lastName', searchLastName.trim());

    setSearchParams(nextParams);
  };

  const handleClearSearch = () => {
    setSearchId('');
    setSearchEmail('');
    setSearchFirstName('');
    setSearchLastName('');
    setSearchParams({});
  };

  useEffect(() => {
    setSearchId(searchParams.get('id') || '');
    setSearchEmail(searchParams.get('email') || '');
    setSearchFirstName(searchParams.get('firstName') || '');
    setSearchLastName(searchParams.get('lastName') || '');
    fetchBetaUsers(1);
  }, [searchParams, fetchBetaUsers]);

  useEffect(() => {
    fetchUnread();
    const timer = window.setInterval(fetchUnread, 30000);
    return () => window.clearInterval(timer);
  }, [fetchUnread]);

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
        </div>
      )}

      <TesterUsersTable
        title="Beta Users"
        users={users}
        usersTotal={usersTotal}
        loading={loadingUsers}
        unreadByUserId={unreadByUserId}
        emptyMessage={isSearching
          ? 'No users found matching the current filters'
          : 'No users marked as Beta testers yet'}
        onOpenChat={setChatUser}
        userProfileFrom="beta-testers"
        pagination={{
          currentPage,
          totalPages,
          onPageChange: fetchBetaUsers,
        }}
        toolbar={(
          <div className="responsive-filter-bar d-flex flex-nowrap align-items-center gap-2 mb-3">
            <form onSubmit={handleSearch} className="d-flex flex-nowrap align-items-center gap-2 flex-grow-1 min-w-0">
              <input
                type="text"
                className="form-control form-control-sm flex-shrink-0"
                placeholder="ID"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                style={{ width: '120px' }}
              />
              <input
                type="text"
                className="form-control form-control-sm flex-shrink-0"
                placeholder="Email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                style={{ width: '160px' }}
              />
              <input
                type="text"
                className="form-control form-control-sm flex-shrink-0"
                placeholder="First Name"
                value={searchFirstName}
                onChange={(e) => setSearchFirstName(e.target.value)}
                style={{ width: '120px' }}
              />
              <input
                type="text"
                className="form-control form-control-sm flex-shrink-0"
                placeholder="Last Name"
                value={searchLastName}
                onChange={(e) => setSearchLastName(e.target.value)}
                style={{ width: '120px' }}
              />
              <button type="submit" className="btn btn-primary btn-sm flex-shrink-0">Search</button>
              {isSearching && (
                <button type="button" className="btn btn-secondary btn-sm flex-shrink-0" onClick={handleClearSearch}>
                  Clear
                </button>
              )}
            </form>
          </div>
        )}
      />

      <UserChatDrawer
        user={chatUser}
        onClose={() => {
          setChatUser(null);
          fetchUnread();
        }}
        onMarkedRead={() => {
          fetchUnread();
          notifyTesterUnreadChanged();
        }}
      />
    </>
  );
};

export default BetaUsersTab;
